import { useEffect, useRef, useState } from 'react';
import { LocalVideoTrack } from 'twilio-video';
import { VideoEffectStore } from '../store/videoEffectModel';
import { BackgroundImage, BlurEffect } from '../videoEffectTypes';

export interface UseTrackChangeReapplyParams {
  isReady: boolean;

  activeBackgroundImageRef: React.MutableRefObject<BackgroundImage | null>;
  applyBackground: (image: BackgroundImage | null) => Promise<void>;
  applyBlur: (effect: BlurEffect) => Promise<void>;
}

export function useTrackChangeReapply({
  isReady,
  activeBackgroundImageRef,
  applyBackground,
  applyBlur,
}: UseTrackChangeReapplyParams) {
  const videoTrack = VideoEffectStore.useStoreState((state) => state.localVideoTrack);
  const activeBackgroundId = VideoEffectStore.useStoreState((state) => state.activeBackgroundId);
  const activeBlurEffect = VideoEffectStore.useStoreState((state) => state.activeBlurEffect);

  const previousTrackRef = useRef<LocalVideoTrack | null>(null);
  const [trackPendingReapply, setTrackPendingReapply] = useState(false);

  useEffect(() => {
    const prevTrack = previousTrackRef.current;
    previousTrackRef.current = videoTrack;

    if (videoTrack && prevTrack && prevTrack !== videoTrack) {
      setTrackPendingReapply(true);
    }
  }, [videoTrack]);

  useEffect(() => {
    if (!trackPendingReapply || !videoTrack || !isReady) return;

    const activeBackgroundImage = activeBackgroundImageRef.current;

    const reapply = async () => {
      if (activeBackgroundId !== null && activeBackgroundImage !== null) {
        await applyBackground(activeBackgroundImage);
      } else if (activeBlurEffect !== 'none') {
        await applyBlur(activeBlurEffect);
      }
    };

    reapply()
      .catch((err) => console.error('Failed to re-apply effect on track change:', err))
      .finally(() => setTrackPendingReapply(false));
  }, [
    trackPendingReapply,
    videoTrack,
    isReady,
    activeBackgroundId,
    activeBlurEffect,
    applyBackground,
    applyBlur,
    activeBackgroundImageRef,
  ]);
}
